import { Component, OnInit } from '@angular/core';
import {forkJoin, of} from 'rxjs';
import {ItemStockInfo} from 'src/app/models/models';
import {Batch_Record, Stock_Record, Store} from 'src/app/models/RestModels';
import {BaseComponent} from '../base/base.component';

export interface BatchAdjustRow {
	batch: string;
	expiration_date: string;
	current_qty: number;
	new_qty: number;
}

interface StoreBatchData {
	records: Batch_Record[];
	rows: BatchAdjustRow[];
}

@Component({
	selector: 'app-adjust-stock',
	templateUrl: './adjust-stock.component.html',
	styleUrls: ['./adjust-stock.component.css']
})
export class AdjustStockComponent extends BaseComponent implements OnInit {

	item_stock_info: ItemStockInfo | null = null;
	store_list: Store[] = [];
	stock_record_dictionary: Record<number, Stock_Record> = {};
	store_batch_data: Record<number, StoreBatchData> = {};

	ngOnInit(): void
	{
		this.stock_record_dictionary = {};
		this.store_batch_data = {} as Record<number, StoreBatchData>;

		this.subs.sink = this.route.paramMap.subscribe((paramMap)=>
		{
			let store_obs = this.store_list.length > 0
					? of({data:this.store_list, total: this.store_list.length})
					: this.rest.store.search({limit:999999, sort_order:['name_ASC']});

			this.subs.sink = forkJoin
			({
				stock_info: this.rest.item_stock_info.get( paramMap.get('item_id') ),
				store: store_obs
			})
			.subscribe((response)=>
			{
				response.stock_info.records
				.forEach((st:Stock_Record)=>
				{
					this.stock_record_dictionary[st.store_id]=st;
				});

				response.store.data.forEach((store:Store)=>
				{
					if( !(store.id  in this.stock_record_dictionary ) )
					{
						this.stock_record_dictionary[ store.id ] = {
							created: new Date(),
							created_by_user_id: this.rest.current_user.id,
							description: '',
							id: 0,
							is_current:null,
							item_id: response.stock_info.item.id,
							movement_qty: 0,
							movement_type:'ADJUSTMENT',
							order_item_id:null,
							previous_qty:0,
							production_item_id:null,
							purchase_detail_id:null,
							qty:0,
							serial_number_record_id:null,
							shipping_item_id: null,
							store_id: store.id,
							updated: new Date(),
							updated_by_user_id: this.rest.current_user.id,
						};
					}
				});

				this.store_list = response.store.data;
				this.item_stock_info = response.stock_info;

				if (this.requiresBatchInfo()) {
					this.loadAllBatchRecords();
				}
			},(error)=>this.showError(error));
		});
	}

	requiresBatchInfo(): boolean
	{
		return !!this.item_stock_info?.item?.batch_option && this.item_stock_info.item.batch_option !== 'NONE';
	}

	requiresBatch(): boolean
	{
		const bo = this.item_stock_info?.item?.batch_option;
		return bo === 'BATCH_ONLY' || bo === 'BATCH_AND_EXPIRATION';
	}

	requiresExpiration(): boolean
	{
		const bo = this.item_stock_info?.item?.batch_option;
		return bo === 'EXPIRATION_ONLY' || bo === 'BATCH_AND_EXPIRATION';
	}

	getBatchRows(store_id: number): BatchAdjustRow[]
	{
		return this.store_batch_data[store_id]?.rows || [];
	}

	loadAllBatchRecords(): void
	{
		let item_id = this.item_stock_info?.item?.id;
		if (!item_id) return;

		let loaders = this.store_list.map(store =>
			this.rest.batch_record.search({
				eq: {
					item_id: item_id,
					store_id: store.id,
					is_current: 1
				},
				gt: {qty: 0},
				limit: 1000
			})
		);

		forkJoin(loaders).subscribe(
			(responses) => {
				responses.forEach((response, index) => {
					let store = this.store_list[index];
					let rows: BatchAdjustRow[] = response.data.map(b => ({
						batch: b.batch,
						expiration_date: b.expiration_date || '',
						current_qty: b.qty,
						new_qty: b.qty
					}));
					this.store_batch_data[store.id] = {
						records: response.data,
						rows: rows
					};
				});
			},
			(error) => this.showError(error)
		);
	}

	hasBatchesForStore(store_id: number): boolean
	{
		const data = this.store_batch_data[store_id];
		return !!data && data.rows.length > 0;
	}

	getStoreTotalQty(store_id: number): number
	{
		const data = this.store_batch_data[store_id];
		if (!data) return 0;
		return data.rows.reduce((sum, r) => sum + r.new_qty, 0);
	}

	newBatchRows: Record<number, { batch: string; expiration_date: string; qty: number }[]> = {};

	addNewBatchRow(store_id: number): void
	{
		if (!this.newBatchRows[store_id]) {
			this.newBatchRows[store_id] = [];
		}
		this.newBatchRows[store_id].push({ batch: '', expiration_date: '', qty: 0 });
	}

	removeNewBatchRow(store_id: number, index: number): void
	{
		if (this.newBatchRows[store_id]) {
			this.newBatchRows[store_id].splice(index, 1);
		}
	}

	save()
	{
		if (this.requiresBatchInfo()) {
			this.saveBatchMode();
		} else {
			this.saveSimpleMode();
		}
	}

	saveSimpleMode(): void
	{
		let info: Stock_Record[] = [];
		for (let i in this.stock_record_dictionary) {
			info.push(this.stock_record_dictionary[i]);
		}
		this.rest.adjustStock(info).subscribe(
			(_response) => {
				this.showSuccess('Se ajustó el inventario exitosamente');
				this.location.back();
			},
			(error) => this.showError(error)
		);
	}

	saveBatchMode(): void
	{
		let hasChanges = false;
		let records: any[] = [];

		for (let store of this.store_list) {
			const data = this.store_batch_data[store.id];
			if (data) {
				for (let row of data.rows) {
					if (row.new_qty !== row.current_qty) {
						hasChanges = true;
						let record: any = {
							item_id: this.item_stock_info?.item?.id,
							store_id: store.id,
							qty: row.new_qty,
							comment: 'Ajuste de inventario'
						};
						if (this.requiresBatch()) {
							record.batch = row.batch;
						}
						if (this.requiresExpiration()) {
							record.expiration_date = row.expiration_date;
						}
						records.push(record);
					}
				}
			}

			if (this.newBatchRows[store.id]) {
				for (let newBatch of this.newBatchRows[store.id]) {
					if (newBatch.qty > 0 && (this.requiresBatch() ? newBatch.batch.trim() !== '' : true)) {
						hasChanges = true;
						let record: any = {
							item_id: this.item_stock_info?.item?.id,
							store_id: store.id,
							qty: newBatch.qty,
							comment: 'Nuevo lote agregado'
						};
						if (this.requiresBatch()) {
							record.batch = newBatch.batch;
						}
						if (this.requiresExpiration()) {
							record.expiration_date = newBatch.expiration_date;
						}
						records.push(record);
					}
				}
			}
		}

		if (!hasChanges) {
			this.showSuccess('No hay cambios por guardar');
			return;
		}

		this.rest.stockAdjust(records).subscribe(
			(_response) => {
				this.showSuccess('Se ajustó el inventario exitosamente');
				this.location.back();
			},
			(error) => this.showError(error)
		);
	}
}
